import { Button, message, Popconfirm, Tag, Typography } from 'antd';
import { useMutation, useQuery } from '@apollo/react-hooks';
import moment from 'moment';
import React from 'react';
import { ALTO_OFFERING_STATUS, REGISTER_ALTO_OFFERING } from '../../pages/apollo_alto';
import { altoOfferingState, backendMessage, registrationMessage } from './altoOffering';

const { Paragraph } = Typography;

// What Alto holds about this note, read beside the listing that names it (TL, 2026-09-12). Registering is
// part of a note's BIRTH — Add Property calls `adminRegisterAltoOffering` right after creating it — not an
// operator action afterwards, so this is STATE and lives in the System panel. One exception keeps a
// button: a note whose registration failed at birth has to be able to RETRY, so "Register in Alto" shows
// only while the state is not "Registered". The state is asked of Alto every time
// (`adminAltoOfferingStatus`); our `altoRegistered` flag alone is not proof.

export function NoteAltoStatus({ investment, refetchNote }) {
  const investmentId = investment._id;
  const { data, loading, error, refetch } = useQuery(ALTO_OFFERING_STATUS, {
    variables: { investmentId },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });
  const [register, { loading: registering }] = useMutation(REGISTER_ALTO_OFFERING);

  const status = data?.adminAltoOfferingStatus;
  const listing = status?.noteListing || investment.noteListing;
  // With Alto unreachable the check fails but the retry stays: registering is what the operator came for.
  const state = status ? altoOfferingState(status) : { action: true };
  const canRetry = state.action && listing && investment.status !== 'deleted';

  const onRegister = async () => {
    try {
      const { data: result } = await register({ variables: { investmentId } });
      message.success(registrationMessage(result.adminRegisterAltoOffering), 8);
    } catch (err) {
      message.error(backendMessage(err), 10);
    }
    // A failed re-read shows as this block's own error state; nothing more to say here.
    await Promise.all([refetch(), refetchNote ? refetchNote() : null]).catch(() => {});
  };

  let current;
  if (loading) current = <Tag>Checking Alto…</Tag>;
  else if (error) current = <Tag color="red">Could not check Alto</Tag>;
  else current = <Tag color={state.color}>{state.label}</Tag>;

  let note = state.note;
  if (error) note = backendMessage(error);
  else if (status?.checkedAt) {
    note = `${note ? `${note} ` : ''}Checked at ${moment(status.checkedAt).format('LT')}.`;
  }

  // Capped on purpose: without a width of its own this cell eats the row and squeezes the listing
  // beside it to one character per line. The whole sentence stays in the tooltip.
  return (
    <div style={{ maxWidth: 240 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {current}
        {canRetry && (
          <Popconfirm
            title={`Creates the offering in Alto under listing ${listing}. This cannot be undone.`}
            okText="Register"
            cancelText="Cancel"
            onConfirm={onRegister}
          >
            <Button size="small" type="primary" loading={registering} disabled={loading}>
              Register in Alto
            </Button>
          </Popconfirm>
        )}
        <Button
          type="link"
          size="small"
          onClick={() => refetch().catch(() => {})}
          disabled={loading || registering}
        >
          Re-check
        </Button>
      </div>
      {note && (
        <Paragraph
          type="secondary"
          ellipsis={{ rows: 2, tooltip: note }}
          style={{ marginTop: 4, marginBottom: 0, fontSize: 12 }}
        >
          {note}
        </Paragraph>
      )}
    </div>
  );
}
